import MainLayout from 'src/layouts/main-layout'
import GuestGuard from 'src/auth/guard/guest-guard'
import PageTitle from 'src/components/atoms/page-title'

import { AuthGuard } from 'src/auth/guard'
import { useTranslation } from 'react-i18next'
import { useRoutes, Navigate, Outlet } from 'react-router-dom'
import { RouteSuspense } from 'src/components/atoms/route-suspense'
import { lazyload } from 'src/utils/lazy-load'
import InfoPage from 'src/components/atoms/info-page'
import SignInPage from 'src/pages/signin'
import NewPassword from 'src/pages/new-password'
import PasswordRecovery from 'src/pages/password-recovery/index'

// Lazy-loaded route components at module scope to avoid remount on location/searchParams change
const Dashboard = lazyload(() => import('src/pages/dashboard'))
const Users = lazyload(() => import('src/pages/users'))
const Report = lazyload(() => import('src/pages/reporting-tool'))
const Sla = lazyload(() => import('src/pages/commercial/case-management/sla'))
const EditAsia = lazyload(() => import('src/pages/orders/b2b-asia/edit-asia'))
const MyOrders = lazyload(() => import('src/pages/commercial/my-orders'))
const ResetPage = lazyload(() => import('src/pages/password-recovery/reset'))
const CloneUser = lazyload(() => import('src/pages/users/clone-user'))
const WorkFlows = lazyload(() => import('src/pages/workflows'))
const CreateUser = lazyload(() => import('src/pages/users/create-user'))
const UpdateUser = lazyload(() => import('src/pages/users/update-user'))
const CreditLimit = lazyload(() => import('src/pages/financial/credit-limit'))
const UserProfiles = lazyload(() => import('src/pages/users/profiles'))
const WorkflowType = lazyload(() => import('src/pages/workflow-type'))
const DelegateUser = lazyload(() => import('src/pages/delegate'))
const ReportingTool = lazyload(() => import('src/pages/reporting-tool'))
const AccountingCost = lazyload(() => import('src/pages/accounting-cost'))
const ManagerialCost = lazyload(() => import('src/pages/managerial-cost'))
const GroupCube = lazyload(() => import('src/pages/price/industrial/group'))
const Contracts = lazyload(() => import('src/pages/commercial/contracts'))
const MyPurchaseOrders = lazyload(() => import('src/pages/orders/my-orders'))
const CommonCrud = lazyload(() => import('src/pages/common-crud'))
const CommonCrudEdit = lazyload(() => import('src/pages/common-crud/edit'))
const OpenInvoices = lazyload(() => import('src/pages/financial/open-invoices'))
const RatingSystem = lazyload(() => import('src/pages/financial/rating-system'))
const SalesReport = lazyload(() => import('src/pages/commercial/sales-report'))
const CommonCrudCreate = lazyload(() => import('src/pages/common-crud/create'))
const WorkflowGroup = lazyload(
  () => import('src/pages/workflows/workflow-group')
)
const UserCompanies = lazyload(
  () => import('src/pages/users/profiles/companies')
)
const CustomReport = lazyload(
  () => import('src/pages/reporting-tool/custom-report')
)
const ContractsReport = lazyload(
  () => import('src/pages/commercial/contracts-report')
)
const AutomotiveDiscountCrud = lazyload(
  () => import('src/pages/commercial/business-partner/automotive-discount/crud')
)
const MassUpdate = lazyload(
  () => import('src/pages/commercial/business-partner/mass-update')
)
const CreateException = lazyload(
  () =>
    import(
      'src/pages/price/material-exception-by-channel/create-exceptions-by-channel'
    )
)
const MaterialExceptionByChannel = lazyload(
  () => import('src/pages/price/material-exception-by-channel')
)
const CreateIndustrialPrice = lazyload(
  () => import('src/pages/price/industrial/create-industrial-table-price')
)
const GroupCubeCrud = lazyload(
  () => import('src/pages/price/industrial/group/crud')
)
const CreatePositioning = lazyload(
  () => import('src/pages/price/automotive/positioning-config/create')
)
const PositioningConfig = lazyload(
  () => import('src/pages/price/automotive/positioning-config')
)
const IndustrialTableConfig = lazyload(
  () => import('src/pages/price/industrial')
)
const CreatePrice = lazyload(
  () => import('src/pages/price/automotive/table-config/pricing/create-price')
)
const EditBusinessPartner = lazyload(
  () => import('src/pages/commercial/business-partner/edit-business-parner')
)
const OfficeHoursCaseManagement = lazyload(
  () => import('src/pages/commercial/case-management/office-hours')
)
const PendingReportByMaterial = lazyload(
  () => import('src/pages/reporting-tool/pending-by-material')
)
const PendingReportByCustomer = lazyload(
  () => import('src/pages/reporting-tool/pending-by-customer')
)
const SalesPerformanceReport = lazyload(
  () => import('src/pages/commercial/sales-performance')
)
const UserBusinessPartners = lazyload(
  () => import('src/pages/users/profiles/business-partners')
)
const PurchaseOrdersReport = lazyload(
  () => import('src/pages/tools/purchase-orders-report')
)
const CreateCaseManagement = lazyload(
  () => import('src/pages/commercial/case-management/create-case-management')
)
const UpdateManagerialCost = lazyload(
  () => import('src/pages/managerial-cost/update-managerial-cost')
)
const OpenInvoicesGrouped = lazyload(
  () => import('src/pages/financial/open-invoices-grouped')
)
const PendingReport = lazyload(
  () => import('src/pages/reporting-tool/report/pending')
)
const ValidationPage = lazyload(
  () => import('src/pages/password-recovery/validation')
)
const UserPermission = lazyload(
  () => import('src/pages/users/profiles/permission')
)
const CreateWorkflow = lazyload(
  () => import('src/pages/workflows/create-workflow')
)
const UpdateWorkflow = lazyload(
  () => import('src/pages/workflows/update-workflow')
)
const CaseManagement = lazyload(
  () => import('src/pages/commercial/case-management')
)
const SalesReport682 = lazyload(
  () => import('src/pages/commercial/sales-report-682')
)
const BusinessPartner = lazyload(
  () => import('src/pages/commercial/business-partner')
)
const ApprovalWorkflow = lazyload(
  () => import('src/pages/workflows/approval-workflow')
)
const UserSalesOffices = lazyload(
  () => import('src/pages/users/profiles/sales-offices')
)
const ImportInfocentre = lazyload(() => import('src/pages/import-infocentre'))
const BrokerCommission = lazyload(
  () => import('src/pages/commercial/broker-commission')
)
const CreateUserProfile = lazyload(
  () => import('src/pages/users/profiles/create')
)
const UserWorkflowTypes = lazyload(
  () => import('src/pages/users/profiles/workflow-types')
)
const CreateCreditLimit = lazyload(
  () => import('src/pages/financial/create-credit-limit')
)
const CreateOfficeHours = lazyload(
  () => import('src/pages/commercial/case-management/create-office-hours')
)
const UpdateWorkflowType = lazyload(
  () => import('src/pages/workflow-type/update-workflow-type')
)
const CreateWorkflowType = lazyload(
  () => import('src/pages/workflow-type/create-workflow-type')
)
const OpenInvoicesReport = lazyload(
  () => import('src/pages/tools/open-invoices-report')
)
const AutomotiveDiscount = lazyload(
  () => import('src/pages/price/automotive/table-config-discount')
)
const CreateWorkflowGroup = lazyload(
  () => import('src/pages/workflows/workflow-group/create-group')
)
const UpdateWorkflowGroup = lazyload(
  () => import('src/pages/workflows/workflow-group/update-workflow')
)
const CreateManagerialCost = lazyload(
  () => import('src/pages/managerial-cost/create-managerial-cost')
)
const Claim = lazyload(() => import('src/pages/commercial/claim'))
const CreateClaim = lazyload(
  () => import('src/pages/commercial/claim/create-claim')
)
const TableConfig = lazyload(
  () => import('src/pages/price/automotive/table-config')
)
const DetailsAsia = lazyload(
  () => import('src/pages/orders/b2b-asia/details-asia')
)
const CreateAsia = lazyload(
  () => import('src/pages/orders/b2b-asia/create-asia')
)
const CreateSla = lazyload(
  () => import('src/pages/commercial/case-management/sla/create-sla')
)
const CrudInternalBrazil = lazyload(
  () => import('src/pages/commercial/internal-brazil/crud-brazil')
)
const CrudInternalArgentina = lazyload(
  () => import('src/pages/commercial/internal-argentina/crud-argentina')
)
const EditSla = lazyload(
  () => import('src/pages/commercial/case-management/sla/update-sla')
)
const EditIndia = lazyload(
  () => import('src/pages/orders/b2b-india/edit-india')
)
const CreateIndia = lazyload(
  () => import('src/pages/orders/b2b-india/create-india')
)
const DetailsIndia = lazyload(
  () => import('src/pages/orders/b2b-india/details-india')
)
const EditEuropeHana = lazyload(
  () => import('src/pages/commercial/edit-europe-hana')
)
const CreateEuropeHana = lazyload(
  () => import('src/pages/commercial/create-europe-hana')
)
const DetailsEuropeHana = lazyload(
  () => import('src/pages/commercial/details-europe-hana')
)
const EditEuropeB2B = lazyload(() => import('src/pages/orders/b2b-europe/edit'))
const CreateEuropeB2B = lazyload(
  () => import('src/pages/orders/b2b-europe/create')
)
const DetailsEuropeB2B = lazyload(
  () => import('src/pages/orders/b2b-europe/details')
)
const CreditLimitApproval = lazyload(
  () => import('src/pages/financial/credit-limit-approval')
)
const CreditLimitDetails = lazyload(
  () => import('src/pages/financial/credit-limit-details')
)
const EditInternalChina = lazyload(
  () => import('src/pages/commercial/internal-china/edit-china')
)
const CreateInternalChina = lazyload(
  () => import('src/pages/commercial/internal-china/create-china')
)
const DetailsInternalChina = lazyload(
  () => import('src/pages/commercial/internal-china/details-china')
)
const EditInternalIndia = lazyload(
  () => import('src/pages/commercial/internal-india/edit-india')
)
const CreateInternalIndia = lazyload(
  () => import('src/pages/commercial/internal-india/create-india')
)
const DetailsInternalIndia = lazyload(
  () => import('src/pages/commercial/internal-india/details-india')
)
const EditHyundaiB2B = lazyload(
  () => import('src/pages/orders/b2b-hyundai/edit-hyundai')
)
const CreateHyundaiB2B = lazyload(
  () => import('src/pages/orders/b2b-hyundai/create-hyundai')
)
const DetailsHyundaiB2B = lazyload(
  () => import('src/pages/orders/b2b-hyundai/details-hyundai')
)
const TableConfigDiscount = lazyload(
  () => import('src/pages/price/automotive/table-config-discount')
)
const CreateDiscount = lazyload(
  () =>
    import(
      'src/pages/price/automotive/table-config-discount/discount-exception/create-discount'
    )
)
const PositioningCube = lazyload(
  () => import('src/pages/price/industrial/positioning')
)
const PositioningCubeCrud = lazyload(
  () => import('src/pages/price/industrial/positioning/crud')
)
const CreateContract = lazyload(
  () => import('src/pages/commercial/contracts/crud')
)
const CrudB2bArgentina = lazyload(
  () => import('src/pages/orders/b2b-argentina/crud-b2b-argentina')
)
const SkuClient = lazyload(
  () => import('src/pages/commercial/edi/client/sku-client')
)
const CreateSkuClient = lazyload(
  () => import('src/pages/commercial/edi/client/sku-client/create-sku-client')
)
const ClientFileName = lazyload(
  () => import('src/pages/commercial/edi/client/file-name')
)
const CreateClientFileName = lazyload(
  () => import('src/pages/commercial/edi/client/file-name/create-file-name')
)
const PackagingClientEDI = lazyload(
  () => import('src/pages/commercial/edi/client/packaging')
)
const PackagingClientEDICrud = lazyload(
  () => import('src/pages/commercial/edi/client/packaging/crud')
)
const PartnerLocationEDI = lazyload(() =>
  import('src/pages/commercial/edi/part-location').then((m) => ({
    default: m.PartnerLocationEDI,
  }))
)
const PartnerLocationEDICrud = lazyload(() =>
  import('src/pages/commercial/edi/part-location/crud').then((m) => ({
    default: m.PartnerLocationEDICrud,
  }))
)
const RefNumber = lazyload(() => import('src/pages/commercial/edi/ref-number'))
const CreateRefNumber = lazyload(
  () => import('src/pages/commercial/edi/ref-number/create-ref-number')
)
const DocumentManagementSGQ = lazyload(() =>
  import('src/pages/sgq/document-management').then((m) => ({
    default: m.DocumentManagementSGQ,
  }))
)

export default function Router() {
  const { i18n } = useTranslation()

  const routes = [
    {
      path: '/',
      element: (
        <GuestGuard>
          <SignInPage title={i18n.t('pageTitle.signIn')} />
        </GuestGuard>
      ),
    },
    {
      path: '/new-password',
      element: <NewPassword title={i18n.t('pageTitle.newPassword')} />,
    },
    {
      path: '/permission-denied',
      element: (
        <AuthGuard>
          <MainLayout>
            <InfoPage
              status="403"
              title="common.error.403.title"
              subTitle="common.error.403.subTitle"
            />
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/password-recovery',
      element: (
        <GuestGuard>
          <Outlet />
        </GuestGuard>
      ),
      title: i18n.t('pageTitle.passwordRecovery'),
      children: [
        {
          element: (
            <PasswordRecovery title={i18n.t('pageTitle.passwordRecovery')} />
          ),
          index: true,
        },
        {
          path: 'validation/:username',
          element: (
            <ValidationPage title={i18n.t('pageTitle.passwordRecovery')} />
          ),
        },
        {
          path: 'validation/:code/:username',
          element: <ResetPage title={i18n.t('pageTitle.passwordRecovery')} />,
        },
      ],
    },
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <Dashboard title={i18n.t('pageTitle.home')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/users',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      title: i18n.t('pageTitle.users'),
      children: [
        {
          element: (
            <RouteSuspense>
              <Users title={i18n.t('pageTitle.users')} />
            </RouteSuspense>
          ),
          index: true,
        },
        {
          path: 'create',
          element: (
            <RouteSuspense>
              <CreateUser title={i18n.t('pageTitle.createUser')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'clone',
          element: (
            <RouteSuspense>
              <CloneUser title={i18n.t('pageTitle.cloneUser')} />
            </RouteSuspense>
          ),
        },
        {
          path: ':id/profiles',
          element: (
            <AuthGuard>
              <Outlet />
            </AuthGuard>
          ),
          title: i18n.t('pageTitle.userProfileDetail'),

          children: [
            {
              element: (
                <RouteSuspense>
                  <UserProfiles title={i18n.t('pageTitle.userProfiles')} />
                </RouteSuspense>
              ),
              index: true,
            },
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateUserProfile
                    title={i18n.t('pageTitle.createProfile')}
                  />
                </RouteSuspense>
              ),
              title: i18n.t('pageTitle.userProfileDetail'),
            },
            {
              path: ':profileId/permission',
              element: (
                <RouteSuspense>
                  <UserPermission title={i18n.t('pageTitle.userPermissions')} />
                </RouteSuspense>
              ),
              title: i18n.t('pageTitle.userProfileDetail'),
            },
            {
              path: ':profileId/companies',
              element: (
                <RouteSuspense>
                  <UserCompanies title={i18n.t('pageTitle.userCompanies')} />
                </RouteSuspense>
              ),
              title: i18n.t('pageTitle.userProfileDetail'),
            },
            {
              path: ':profileId/sales-offices',
              element: (
                <RouteSuspense>
                  <UserSalesOffices title={i18n.t('pageTitle.userSOffices')} />
                </RouteSuspense>
              ),
            },
            {
              path: ':profileId/business-partners',
              element: (
                <RouteSuspense>
                  <UserBusinessPartners title={i18n.t('pageTitle.userBP')} />
                </RouteSuspense>
              ),
              title: i18n.t('pageTitle.userProfileDetail'),
            },
            {
              path: ':profileId/workflow-types',
              element: (
                <RouteSuspense>
                  <UserWorkflowTypes
                    title={i18n.t('pageTitle.userWorkflowTypes')}
                  />
                </RouteSuspense>
              ),
              title: i18n.t('pageTitle.userProfileDetail'),
            },
          ],
        },
        {
          path: ':id',
          element: (
            <RouteSuspense>
              <UpdateUser title={i18n.t('pageTitle.updateUser')} />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/workflows',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      title: i18n.t('pageTitle.users'),
      children: [
        {
          element: (
            <RouteSuspense>
              <WorkFlows title={i18n.t('pageTitle.workflows')} />
            </RouteSuspense>
          ),
          index: true,
        },
        {
          path: 'delegate',
          element: (
            <RouteSuspense>
              <DelegateUser title={i18n.t('pageTitle.delegateUser')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <RouteSuspense>
              <CreateWorkflow title={i18n.t('pageTitle.createWorkflow')} />
            </RouteSuspense>
          ),
        },
        {
          path: ':id',
          element: (
            <RouteSuspense>
              <UpdateWorkflow title={i18n.t('pageTitle.editWorkflow')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'approval/:id',
          element: (
            <RouteSuspense>
              <ApprovalWorkflow title={i18n.t('pageTitle.approvalWorkflow')} />
            </RouteSuspense>
          ),
        },
        {
          element: <Outlet />,
          path: 'types',
          children: [
            {
              element: (
                <RouteSuspense>
                  <WorkflowType title={i18n.t('pageTitle.workflowType')} />
                </RouteSuspense>
              ),
              index: true,
            },
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateWorkflowType
                    title={i18n.t('pageTitle.createWorkflowType')}
                  />
                </RouteSuspense>
              ),
            },
            {
              path: ':id',
              element: (
                <RouteSuspense>
                  <UpdateWorkflowType
                    title={i18n.t('pageTitle.createWorkflowType')}
                  />
                </RouteSuspense>
              ),
            },
          ],
        },
        {
          element: <Outlet />,
          path: 'groups',
          children: [
            {
              element: (
                <RouteSuspense>
                  <WorkflowGroup title={i18n.t('pageTitle.workflowGroup')} />
                </RouteSuspense>
              ),
              index: true,
            },
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateWorkflowGroup
                    title={i18n.t('pageTitle.createWorkflowGroup')}
                  />
                </RouteSuspense>
              ),
            },
            {
              path: ':id',
              element: (
                <RouteSuspense>
                  <UpdateWorkflowGroup
                    title={i18n.t('pageTitle.updateWorkflowGroup')}
                  />
                </RouteSuspense>
              ),
            },
          ],
        },
      ],
    },
    {
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <OpenInvoices title={i18n.t('pageTitle.openInvoices')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
      path: '/open-invoices',
    },
    {
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <OpenInvoicesGrouped title={i18n.t('pageTitle.openInvoices')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
      path: '/open-invoices-grouped',
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      title: i18n.t('pageTitle.managerialCost'),
      children: [
        {
          path: 'managerial-cost',
          element: <Outlet />,
          children: [
            {
              element: (
                <RouteSuspense>
                  <ManagerialCost title={i18n.t('pageTitle.managerialCost')} />
                </RouteSuspense>
              ),
              index: true,
            },
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateManagerialCost
                    title={i18n.t('pageTitle.managerialCost')}
                  />
                </RouteSuspense>
              ),
            },
            {
              path: ':id',
              element: (
                <RouteSuspense>
                  <UpdateManagerialCost
                    title={i18n.t('pageTitle.managerialCost')}
                  />
                </RouteSuspense>
              ),
            },
          ],
        },
      ],
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      title: i18n.t('pageTitle.accountingCost'),
      children: [
        {
          path: '/cost-accounting',
          element: <Outlet />,
          children: [
            {
              element: (
                <RouteSuspense>
                  <AccountingCost title={i18n.t('pageTitle.accountingCost')} />
                </RouteSuspense>
              ),
              index: true,
            },
          ],
        },
      ],
    },
    {
      path: '/sales-report',
      element: (
        <AuthGuard>
          <MainLayout contentId="scrollableTable">
            <RouteSuspense>
              <SalesReport title={i18n.t('pageTitle.salesReport')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/sales-performance-report',
      element: (
        <AuthGuard>
          <MainLayout contentId="scrollableTable">
            <RouteSuspense>
              <SalesPerformanceReport
                title={i18n.t('pageTitle.salesPerformanceReport')}
              />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/broker-commission',
      element: (
        <AuthGuard>
          <MainLayout contentId="scrollableTable">
            <RouteSuspense>
              <BrokerCommission title={i18n.t('pageTitle.salesReport')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/sales-report-682',
      element: (
        <AuthGuard>
          <MainLayout contentId="scrollableTable">
            <RouteSuspense>
              <SalesReport682 title={i18n.t('pageTitle.salesReport682')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/reporting-tool',
      element: (
        <AuthGuard>
          <MainLayout contentId="scrollableTable">
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: (
            <RouteSuspense>
              <ReportingTool title={i18n.t('pageTitle.reportingTool')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'report',
          element: (
            <RouteSuspense>
              <Report title={i18n.t('pageTitle.report')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'pending-report',
          element: (
            <RouteSuspense>
              <PendingReport title={i18n.t('pageTitle.pendingReport')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'pending-by-material',
          element: (
            <RouteSuspense>
              <PendingReportByMaterial
                title={i18n.t('pageTitle.pendingReportByMaterial')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'pending-by-customer',
          element: (
            <RouteSuspense>
              <PendingReportByCustomer
                title={i18n.t('pageTitle.pendingReportByCustomer')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'custom-report',
          element: (
            <RouteSuspense>
              <CustomReport title={i18n.t('pageTitle.customReport')} />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/my-orders',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: (
            <RouteSuspense>
              <MyOrders title={i18n.t('pageTitle.myOrders')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'internal-europe/update/:id',
          element: (
            <RouteSuspense>
              <EditEuropeHana title={i18n.t('pageTitle.editSalesOrder')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'internal-europe/details/:id',
          element: (
            <RouteSuspense>
              <DetailsEuropeHana title={i18n.t('pageTitle.salesOrderDetail')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'internal-china/details/:id',
          element: (
            <RouteSuspense>
              <EditInternalChina title={i18n.t('pageTitle.editSalesOrder')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'internal-china/details/:id',
          element: (
            <RouteSuspense>
              <DetailsInternalChina
                title={i18n.t('pageTitle.salesOrderDetail')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'internal-india/update/:id',
          element: (
            <RouteSuspense>
              <EditInternalIndia title={i18n.t('pageTitle.editSalesOrder')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'internal-india/details/:id',
          element: (
            <RouteSuspense>
              <DetailsInternalIndia
                title={i18n.t('pageTitle.salesOrderDetail')}
              />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/create-europe-hana',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <CreateEuropeHana title={i18n.t('pageTitle.createSalesOrder')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/internal-india/create',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <CreateInternalIndia
                title={i18n.t('pageTitle.createSalesOrder')}
              />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/internal-china/create',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <CreateInternalChina
                title={i18n.t('pageTitle.createSalesOrder')}
              />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/internal-brazil',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <Outlet />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          path: 'create',
          element: (
            <CrudInternalBrazil title={i18n.t('pageTitle.createSalesOrder')} />
          ),
        },
        {
          path: 'update/:id',
          element: (
            <CrudInternalBrazil title={i18n.t('pageTitle.createSalesOrder')} />
          ),
        },
        {
          path: 'details/:id',
          element: (
            <CrudInternalBrazil
              isReadOnly
              title={i18n.t('pageTitle.createSalesOrder')}
            />
          ),
        },
        {
          path: 'release/:id',
          element: (
            <CrudInternalBrazil
              isReleasing
              isReadOnly
              title={i18n.t('pageTitle.releaseSalesOrder')}
            />
          ),
        },
      ],
    },
    {
      path: '/internal-argentina',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <Outlet />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          path: 'create',
          element: (
            <CrudInternalArgentina
              title={i18n.t('pageTitle.createSalesOrder')}
            />
          ),
        },
        {
          path: 'update/:id',
          element: (
            <CrudInternalArgentina title={i18n.t('pageTitle.editSalesOrder')} />
          ),
        },
        {
          path: 'details/:id',
          element: (
            <CrudInternalArgentina
              isReadOnly
              title={i18n.t('pageTitle.detailSalesOrder')}
            />
          ),
        },
        {
          path: 'release/:id',
          element: (
            <CrudInternalArgentina
              isReleasing
              isReadOnly
              title={i18n.t('pageTitle.releaseSalesOrder')}
            />
          ),
        },
      ],
    },
    {
      path: '/case-management',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: (
            <RouteSuspense>
              <CaseManagement
                title={i18n.t('customerServices.caseManagement.title')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <RouteSuspense>
              <CreateCaseManagement
                title={i18n.t('pageTitle.createCaseManagement')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'update/:id',
          element: (
            <RouteSuspense>
              <CreateCaseManagement
                title={i18n.t('pageTitle.editCaseManagement')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'detail/:id',
          element: (
            <RouteSuspense>
              <CreateCaseManagement
                readOnly={true}
                title={i18n.t('pageTitle.detailsCaseManagement')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'office-hours/create',
          element: (
            <RouteSuspense>
              <CreateOfficeHours
                title={i18n.t(
                  'customerServices.caseManagement.officeHours.title'
                )}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'office-hours/create/:id',
          element: (
            <RouteSuspense>
              <CreateOfficeHours
                title={i18n.t(
                  'customerServices.caseManagement.officeHours.title'
                )}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'office-hours',
          element: (
            <RouteSuspense>
              <OfficeHoursCaseManagement
                title={i18n.t(
                  'customerServices.caseManagement.officeHours.title'
                )}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'sla',
          element: (
            <RouteSuspense>
              <Sla title={i18n.t('pageTitle.sla')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'sla/create',
          element: (
            <RouteSuspense>
              <CreateSla title={i18n.t('pageTitle.createSla')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'sla/update/:id',
          element: (
            <RouteSuspense>
              <EditSla title={i18n.t('pageTitle.editSla')} />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/claim',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: (
            <RouteSuspense>
              <Claim title={i18n.t('pageTitle.claim')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'update/:id',
          element: (
            <RouteSuspense>
              <CreateClaim title={i18n.t('pageTitle.editClaim')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'detail/:id',
          element: (
            <RouteSuspense>
              <CreateClaim
                readOnly={true}
                title={i18n.t('pageTitle.detailClaim')}
              />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/business-partner',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: (
            <RouteSuspense>
              <BusinessPartner title={i18n.t('pageTitle.businessPartner')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'update/:id',
          element: (
            <RouteSuspense>
              <EditBusinessPartner
                title={i18n.t('pageTitle.editBusinessPartner')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'detail/:id',
          element: (
            <RouteSuspense>
              <EditBusinessPartner
                readOnly={true}
                title={i18n.t('pageTitle.detailBusinessPartner')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'mass-update',
          element: (
            <RouteSuspense>
              <MassUpdate title={i18n.t('pageTitle.massUpdate')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive-discount',
          element: (
            <RouteSuspense>
              <AutomotiveDiscount
                title={i18n.t('pageTitle.automotiveDiscount')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive-discount/create',
          element: (
            <RouteSuspense>
              <AutomotiveDiscountCrud
                title={i18n.t('pageTitle.createAutomotiveDiscount')}
              />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/price',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          path: 'automotive',
          element: (
            <RouteSuspense>
              <TableConfig title={i18n.t('pageTitle.tableConfigDiscount')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/create',
          element: (
            <RouteSuspense>
              <CreatePrice title={i18n.t('pageTitle.createPrice')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/update/:id',
          element: (
            <RouteSuspense>
              <CreatePrice title={i18n.t('pageTitle.editPrice')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/detail/:id',
          element: (
            <RouteSuspense>
              <CreatePrice
                readOnly={true}
                title={i18n.t('pageTitle.detailPrice')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/positioning',
          element: (
            <RouteSuspense>
              <PositioningConfig
                title={i18n.t('pageTitle.configPositioning')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/positioning/create',
          element: (
            <RouteSuspense>
              <CreatePositioning
                title={i18n.t('pageTitle.createPositioning')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/positioning/update/:id',
          element: (
            <RouteSuspense>
              <CreatePositioning title={i18n.t('pageTitle.editPositioning')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/positioning/detail/:id',
          element: (
            <RouteSuspense>
              <CreatePositioning
                title={i18n.t('pageTitle.detailPositioning')}
                readOnly={true}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/discount-exception',
          element: (
            <RouteSuspense>
              <TableConfigDiscount
                title={i18n.t('pageTitle.tableConfigDiscount')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/discount-exception/create',
          element: (
            <RouteSuspense>
              <CreateDiscount title={i18n.t('pageTitle.createDiscount')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/discount-exception/update/:id',
          element: (
            <RouteSuspense>
              <CreateDiscount title={i18n.t('pageTitle.editDiscount')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'automotive/discount-exception/detail/:id',
          element: (
            <RouteSuspense>
              <CreateDiscount
                readOnly={true}
                title={i18n.t('pageTitle.detailDiscount')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial',
          element: (
            <RouteSuspense>
              <IndustrialTableConfig
                title={i18n.t('pageTitle.industrialTableConfig')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/create',
          element: (
            <RouteSuspense>
              <CreateIndustrialPrice
                title={i18n.t('pageTitle.createIndustrialPrice')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/update/:id',
          element: (
            <RouteSuspense>
              <CreateIndustrialPrice
                title={i18n.t('pageTitle.editIndustrialPrice')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/detail/:id',
          element: (
            <RouteSuspense>
              <CreateIndustrialPrice
                readOnly={true}
                title={i18n.t('pageTitle.detailIndustrialPrice')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/positioning-cube',
          element: (
            <RouteSuspense>
              <PositioningCube
                title={i18n.t('pageTitle.listPositioningCube')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/positioning-cube/create',
          element: (
            <RouteSuspense>
              <PositioningCubeCrud
                title={i18n.t('pageTitle.createPositioningCube')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/positioning-cube/update/:id',
          element: (
            <RouteSuspense>
              <PositioningCubeCrud
                title={i18n.t('pageTitle.editPositioningCube')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/positioning-cube/detail/:id',
          element: (
            <RouteSuspense>
              <PositioningCubeCrud
                title={i18n.t('pageTitle.detailPositioningCube')}
                readOnly
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'material-exception-by-channel',
          element: (
            <MaterialExceptionByChannel
              title={i18n.t('pageTitle.materialExceptionsByChannel')}
            />
          ),
        },
        {
          path: 'material-exception-by-channel/create',
          element: (
            <CreateException
              title={i18n.t('pageTitle.createMaterialExceptionsByChannel')}
            />
          ),
        },
        {
          path: 'material-exception-by-channel/update/:id',
          element: (
            <CreateException
              title={i18n.t(
                'pageTitle.editmaterialMaterialExceptionsByChannel'
              )}
            />
          ),
        },
        {
          path: 'material-exception-by-channel/detail/:id',
          element: (
            <CreateException
              readOnly={true}
              title={i18n.t('pageTitle.detailMaterialExceptionsByChannel')}
            />
          ),
        },
        {
          path: 'industrial/group',
          element: (
            <RouteSuspense>
              <GroupCube title={i18n.t('pageTitle.groupCube')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/group/create',
          element: (
            <RouteSuspense>
              <GroupCubeCrud title={i18n.t('pageTitle.createGroupCube')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/group/update/:id',
          element: (
            <RouteSuspense>
              <GroupCubeCrud title={i18n.t('pageTitle.updateGroupCube')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'industrial/group/detail/:id',
          element: (
            <RouteSuspense>
              <GroupCubeCrud
                title={i18n.t('pageTitle.detailGroupCube')}
                readOnly
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'material-exception-by-channel',
          element: (
            <RouteSuspense>
              <MaterialExceptionByChannel
                title={i18n.t('pageTitle.materialExceptionsByChannel')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'material-exception-by-channel/create',
          element: (
            <RouteSuspense>
              <CreateException
                title={i18n.t('pageTitle.createMaterialExceptionsByChannel')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'material-exception-by-channel/update/:id',
          element: (
            <RouteSuspense>
              <CreateException
                title={i18n.t(
                  'pageTitle.editmaterialMaterialExceptionsByChannel'
                )}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'import',
          element: (
            <RouteSuspense>
              <CreateException
                readOnly={true}
                title={i18n.t('pageTitle.detailMaterialExceptionsByChannel')}
              />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/contracts',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: (
            <RouteSuspense>
              <Contracts title={i18n.t('pageTitle.contracts')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <RouteSuspense>
              <CreateContract title={i18n.t('pageTitle.createContract')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'detail/:id',
          element: (
            <RouteSuspense>
              <CreateContract
                readOnly={true}
                title={i18n.t('pageTitle.detailContract')}
              />
            </RouteSuspense>
          ),
        },

        {
          path: 'update/:id',
          element: (
            <RouteSuspense>
              <CreateContract title={i18n.t('pageTitle.editContract')} />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/contracts-report',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: (
            <RouteSuspense>
              <ContractsReport title={i18n.t('pageTitle.contractsReport')} />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/import-infocentre',
      element: (
        <AuthGuard>
          <MainLayout contentId="scrollableTable">
            <RouteSuspense>
              <ImportInfocentre title={i18n.t('pageTitle.importInfocentre')} />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/common/:crudName',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <CommonCrud />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/common/:crudName/create',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <CommonCrudCreate />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/common/:crudName/edit',
      element: (
        <AuthGuard>
          <MainLayout>
            <RouteSuspense>
              <CommonCrudEdit />
            </RouteSuspense>
          </MainLayout>
        </AuthGuard>
      ),
    },
    {
      path: '/credit-limit',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      title: i18n.t('pageTitle.users'),
      children: [
        {
          element: (
            <RouteSuspense>
              <CreditLimit title={i18n.t('pageTitle.creditLimit')} />
            </RouteSuspense>
          ),
          index: true,
        },
        {
          path: 'create',
          element: (
            <RouteSuspense>
              <CreateCreditLimit
                title={i18n.t('pageTitle.createCreditLimit')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'approval/:id',
          element: (
            <RouteSuspense>
              <CreditLimitApproval
                title={i18n.t('pageTitle.createCreditLimit')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'details/:id',
          element: (
            <RouteSuspense>
              <CreditLimitDetails
                title={i18n.t('pageTitle.creditLimitDetails')}
              />
            </RouteSuspense>
          ),
        },
        {
          path: 'rating-system',
          element: (
            <RouteSuspense>
              <RatingSystem title={i18n.t('pageTitle.createCreditLimit')} />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/orders',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          path: 'my-purchase-orders',
          element: (
            <RouteSuspense>
              <MyPurchaseOrders title={i18n.t('pageTitle.myPurchaseOrders')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'b2b-hyundai',
          element: <Outlet />,
          children: [
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateHyundaiB2B
                    title={i18n.t('pageTitle.createSalesOrder')}
                  />
                </RouteSuspense>
              ),
            },
            {
              path: 'update/:id',
              element: (
                <RouteSuspense>
                  <EditHyundaiB2B title={i18n.t('pageTitle.editSalesOrder')} />
                </RouteSuspense>
              ),
            },
            {
              path: 'details/:id',
              element: (
                <RouteSuspense>
                  <DetailsHyundaiB2B
                    title={i18n.t('pageTitle.salesOrderDetail')}
                  />
                </RouteSuspense>
              ),
            },
          ],
        },
        {
          path: 'b2b-europe',
          element: <Outlet />,
          children: [
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateEuropeB2B
                    title={i18n.t('pageTitle.createSalesOrder')}
                  />
                </RouteSuspense>
              ),
            },
            {
              path: 'update/:id',
              element: (
                <RouteSuspense>
                  <EditEuropeB2B title={i18n.t('pageTitle.editSalesOrder')} />
                </RouteSuspense>
              ),
            },
            {
              path: 'details/:id',
              element: (
                <RouteSuspense>
                  <DetailsEuropeB2B
                    title={i18n.t('pageTitle.salesOrderDetail')}
                  />
                </RouteSuspense>
              ),
            },
          ],
        },
        {
          path: 'b2b-india',
          element: <Outlet />,
          children: [
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateIndia title={i18n.t('pageTitle.createSalesOrder')} />
                </RouteSuspense>
              ),
            },
            {
              path: 'update/:id',
              element: (
                <RouteSuspense>
                  <EditIndia title={i18n.t('pageTitle.editSalesOrder')} />
                </RouteSuspense>
              ),
            },
            {
              path: 'details/:id',
              element: (
                <RouteSuspense>
                  <DetailsIndia title={i18n.t('pageTitle.salesOrderDetail')} />
                </RouteSuspense>
              ),
            },
          ],
        },
        {
          path: 'b2b-asia',
          element: <Outlet />,
          children: [
            {
              path: 'create',
              element: (
                <RouteSuspense>
                  <CreateAsia title={i18n.t('pageTitle.createSalesOrder')} />
                </RouteSuspense>
              ),
            },
            {
              path: 'update/:id',
              element: (
                <RouteSuspense>
                  <EditAsia title={i18n.t('pageTitle.editSalesOrder')} />
                </RouteSuspense>
              ),
            },
            {
              path: 'details/:id',
              element: (
                <RouteSuspense>
                  <DetailsAsia title={i18n.t('pageTitle.salesOrderDetail')} />
                </RouteSuspense>
              ),
            },
          ],
        },
        {
          path: 'b2b-argentina',
          element: <Outlet />,
          children: [
            {
              path: 'create',
              element: (
                <CrudB2bArgentina
                  title={i18n.t('pageTitle.createSalesOrder')}
                />
              ),
            },
            {
              path: 'update/:id',
              element: (
                <CrudB2bArgentina title={i18n.t('pageTitle.editSalesOrder')} />
              ),
            },
            {
              path: 'details/:id',
              element: (
                <CrudB2bArgentina
                  isReadOnly
                  title={i18n.t('pageTitle.salesOrderDetail')}
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: '/tools-reports',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          path: 'open-invoices',
          element: (
            <RouteSuspense>
              <OpenInvoicesReport title={i18n.t('pageTitle.openInvoices')} />
            </RouteSuspense>
          ),
        },
        {
          path: 'purchase-orders',
          element: (
            <RouteSuspense>
              <PurchaseOrdersReport
                title={i18n.t('pageTitle.purchaseOrdersReport')}
              />
            </RouteSuspense>
          ),
        },
      ],
    },
    {
      path: '/edi',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        { path: 'sku-client', element: <SkuClient title="SKU Client" /> },
        {
          path: 'sku-client/create',
          element: (
            <CreateSkuClient title={i18n.t('pageTitle.createSkuClient')} />
          ),
        },
        {
          path: 'sku-client/update/:id',
          element: (
            <CreateSkuClient title={i18n.t('pageTitle.editSkuClient')} />
          ),
        },
        {
          path: 'sku-client/details/:id',
          element: (
            <CreateSkuClient
              readOnly
              title={i18n.t('pageTitle.detailSkuClient')}
            />
          ),
        },
        {
          path: 'client/file-name',
          element: (
            <ClientFileName title={i18n.t('pageTitle.clientFileName')} />
          ),
        },
        {
          path: 'client/file-name/create',
          element: (
            <CreateClientFileName title={i18n.t('pageTitle.clientFileName')} />
          ),
        },
        {
          path: 'client/packaging',
          element: (
            <PackagingClientEDI
              title={i18n.t('pageTitle.clientEdiPackaging')}
            />
          ),
        },
        {
          path: 'client/packaging/create',
          element: (
            <PackagingClientEDICrud
              title={i18n.t('pageTitle.createClientEdiPackaging')}
            />
          ),
        },
        {
          path: 'client/packaging/update/:id',
          element: (
            <PackagingClientEDICrud
              title={i18n.t('pageTitle.updateClientEdiPackaging')}
            />
          ),
        },
        {
          path: 'client/packaging/detail/:id',
          element: (
            <PackagingClientEDICrud
              readOnly={true}
              title={i18n.t('pageTitle.detailsClientEdiPackaging')}
            />
          ),
        },
        { path: 'sku-edi', element: <SkuClient title="SKU Client" /> },
        {
          path: 'partner-location',
          element: <Outlet />,
          children: [
            {
              index: true,
              element: (
                <PartnerLocationEDI
                  title={i18n.t('pageTitle.ediPartLocation')}
                />
              ),
            },
            {
              path: 'create',
              element: (
                <PartnerLocationEDICrud
                  title={i18n.t('pageTitle.createEdiPartLocation')}
                />
              ),
            },
            {
              path: 'update/:id',
              element: (
                <PartnerLocationEDICrud
                  title={i18n.t('pageTitle.updateEdiPartLocation')}
                />
              ),
            },
            {
              path: 'detail/:id',
              element: (
                <PartnerLocationEDICrud
                  readOnly={true}
                  title={i18n.t('pageTitle.detailsEdiPartLocation')}
                />
              ),
            },
          ],
        },
        {
          path: 'ref-number',
          element: <RefNumber title={i18n.t('pageTitle.ediRefNumber')} />,
        },
        {
          path: 'ref-number/create',
          element: (
            <CreateRefNumber title={i18n.t('pageTitle.createRefNumber')} />
          ),
        },
        {
          path: 'ref-number/update/:id',
          element: (
            <CreateRefNumber title={i18n.t('pageTitle.editRefNumber')} />
          ),
        },
        {
          path: 'ref-number/detail/:id',
          element: (
            <CreateRefNumber
              readOnly={true}
              title={i18n.t('pageTitle.detailsRefNumber')}
            />
          ),
        },
      ],
    },
    {
      path: '/sgq',
      element: (
        <AuthGuard>
          <MainLayout>
            <Outlet />
          </MainLayout>
        </AuthGuard>
      ),
      children: [
        {
          path: 'document-management',
          element: (
            <DocumentManagementSGQ
              title={i18n.t('pageTitle.documentManagementSGQ')}
            />
          ),
        },
      ],
    },
    {
      path: '*',
      element: (
        <GuestGuard>
          <Navigate to="/" replace />
        </GuestGuard>
      ),
    },
  ]

  const routesWithTitle = useRoutes(
    routes.map((route) => ({
      ...route,
      element: (
        <>
          <PageTitle title={route.title ?? i18n.t('pageTitle.prefix')} />
          {route.element}
        </>
      ),
    }))
  )

  return routesWithTitle
}
